/* eslint-disable */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ui';
import buttonStyles from '../../shared/ui/button/button.module.css';
import { ActionButtonSpinner, getActionButtonLoadingClassName } from '../../shared/ui/actionButtonLoader';
import { api } from '../../redux/api';

const PAID_SESSION_STATES = ['DONE', 'PARTIALLY_REFUNDED', 'OVERCHARGED'];

function hasPaidChargingSession(historyData) {
  if (!Array.isArray(historyData)) return false;
  return historyData.some(item => PAID_SESSION_STATES.includes(item.state));
}

/**
 * Shown when station name contains allowreset, user has a completed paid session,
 * and protocol is OCPP or ROAMING_TOKA (same rules as backend).
 */
export function canShowRestartStation({ station, stationNumber, historyData, isHistoryReady }) {
  if (!isHistoryReady || !station || !stationNumber) return false;
  const allowReset = (station.name || '').toLowerCase().includes('allowreset');
  const protocol = station.protocolType;
  const supported = (protocol === 'OCPP' || protocol === 'ROAMING_TOKA') && !!station.deviceId;
  return allowReset && hasPaidChargingSession(historyData) && supported;
}

export default function RestartStationButton({ station, stationNumber, historyData, isHistoryReady }) {
  const { t } = useTranslation();
  const [restartStation, { isLoading }] = api.useRestartStationMutation();
  const [blocked, setBlocked] = useState(false);

  if (!canShowRestartStation({ station, stationNumber, historyData, isHistoryReady })) return null;

  const protocol = station.protocolType;
  const deviceId = station.deviceId;

  const parseErrorMessage = err => {
    const d = err?.data;
    if (typeof d === 'string') return d;
    if (d?.detail != null) {
      return typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail);
    }
    if (d?.message) return d.message;
    return t('restartStation.errorFailed');
  };

  const onClick = async () => {
    if (blocked || isLoading) return;

    if (protocol === 'OCPP') {
      try {
        await navigator.clipboard.writeText(deviceId);
      } catch (_) {
        /* non-fatal */
      }
      if (!window.confirm(t('restartStation.confirmOcpp', { port: stationNumber }))) return;
    } else {
      if (!window.confirm(t('restartStation.confirmToka'))) return;
    }

    setBlocked(true);
    try {
      await restartStation({ stationNumber }).unwrap();
      window.alert(protocol === 'ROAMING_TOKA' ? t('restartStation.successToka') : t('restartStation.successOcpp'));
    } catch (e) {
      window.alert(parseErrorMessage(e));
    } finally {
      setBlocked(false);
    }
  };

  const isBusy = isLoading || blocked;

  return (
    <Button
      variant="outline"
      className={`${buttonStyles.buttonPhoneLabel} ${getActionButtonLoadingClassName(isBusy, true)}`}
      style={{
        display: 'flex',
        gap: '12px',
        margin: '24px auto 0',
        width: '86%',
      }}
      disabled={isBusy}
      aria-busy={isBusy}
      onClick={onClick}
    >
      {isBusy ? <ActionButtonSpinner /> : <span>{t('restartStation.button')}</span>}
    </Button>
  );
}
